import { DeleteOutline } from "@mui/icons-material";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { IntegrationDetail } from "features/integrationsBeta/integrationsBeta";
import { useState } from "react";
import DeleteIntegrationDialog from "./DeleteIntegrationDialog";
import SectionCard from "./SectionCard";
import { usePendingChanges } from "./pending/PendingChangesContext";

interface Props {
  integration: IntegrationDetail;
  /** Fired after a successful delete so the page can navigate away. */
  onDeleted?: () => void;
}

/**
 * Irreversible actions for one integration.
 *
 * The impact report is fetched only once the operator opens the dialog - this
 * tab must not cost a request just by being visited.
 */
export default function DangerZoneSection({ integration, onDeleted }: Props) {
  const [open, setOpen] = useState(false);
  const { count, clear } = usePendingChanges();

  const handleDeleted = () => {
    // Drop staged edits first: it clears the Header's unsaved-changes flag and
    // the beforeunload listener, and stops the Settings reconciliation effect
    // from firing a "pending changes discarded" toast on the way out.
    clear();
    onDeleted?.();
  };

  return (
    <SectionCard title="Danger zone">
      <Stack spacing={1.5}>
        <Typography
          variant="subtitle2"
          sx={{
            color: "error.main",
            fontWeight: 800,
            textTransform: "uppercase",
          }}
        >
          Warning
        </Typography>
        <Typography variant="body2">
          Deleting an integration removes its pipeline steps, its schedules
          (including the schedules in AWS EventBridge), its webhook triggers
          together with their dedicated connections and secrets, its run history
          and its Data-Storage folder. Connections shared with other
          integrations, the shared config profile and the archived files in S3
          are kept.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You will see the exact list of what is removed and what is kept before
          confirming.
        </Typography>
        {count > 0 && (
          <Alert severity="info">
            You have {count} unsaved settings change
            {count === 1 ? "" : "s"}. Deleting the integration discards them.
          </Alert>
        )}
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteOutline />}
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Delete integration
        </Button>
      </Stack>

      <DeleteIntegrationDialog
        open={open}
        integration={{
          id: integration.id,
          name: integration.name,
          client_name: integration.client_name,
          is_active: integration.is_active,
        }}
        onClose={() => setOpen(false)}
        onDeleted={handleDeleted}
      />
    </SectionCard>
  );
}
